import type { Ref } from "preact";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon, type IconName } from "../icons/Icon.tsx";

export type MenuPanel = "preferences" | "contents" | "bookmarks" | "search" | "book";

interface Props {
  title: string;
  section?: string;
  excerpt: boolean;
  backUrl?: string;
  bookmarked: boolean;
  bookmarksEnabled: boolean;
  searchEnabled: boolean;
  /** Surlignages et notes : l'entrée « Signets » devient « Notes et signets ». */
  notesEnabled: boolean;
  chatAvailable: boolean;
  chatOpen: boolean;
  openPanel: MenuPanel | null;
  onToggleBookmark(): void;
  onToggleChat(): void;
  /** Reading aloud with the voice of the device: undefined when the book or the browser cannot. */
  readAloud?: ReadAloudControl;
  onFullscreen(): void;
  onPanel(panel: MenuPanel, anchor: HTMLElement): void;
  buttonRefs: Record<MenuPanel, Ref<HTMLButtonElement>>;
  /** Extrait dans la fiche produit : ni titre (déjà dans la fenêtre du site), ni chat, ni plein écran. */
  embedded?: boolean;
}

const messages = defineMessages(
  {
    preferences: "Réglages",
    contents: "Table des matières",
    bookmarks: "Signets",
    search: "Rechercher",
    notes: "Notes et signets",
    closeBook: "Fermer le livre",
    addBookmark: "Ajouter un signet",
    removeBookmark: "Retirer le signet",
    excerpt: "Extrait",
    bookMenu: "Menu du livre",
    chat: "Chat",
    showChat: "Afficher le chat",
    hideChat: "Masquer le chat",
    fullscreen: "Plein écran",
    fullscreenLabel: "Plein écran : afficher seulement le texte",
    readAloud: "Lire à voix haute",
    readAloudStop: "Arrêter la lecture",
    book: "Fiche du livre",
  },
  {
    preferences: "Settings",
    contents: "Contents",
    bookmarks: "Bookmarks",
    search: "Search",
    notes: "Notes and bookmarks",
    closeBook: "Close book",
    addBookmark: "Add bookmark",
    removeBookmark: "Remove bookmark",
    excerpt: "Excerpt",
    bookMenu: "Book menu",
    chat: "Chat",
    showChat: "Show chat",
    hideChat: "Hide chat",
    fullscreen: "Full screen",
    fullscreenLabel: "Full screen: show only the text",
    readAloud: "Read aloud",
    readAloudStop: "Stop reading",
    book: "About this book",
  },
);

const menu: Array<{ panel: MenuPanel; icon: IconName }> = [
  { panel: "preferences", icon: "preferences" },
  { panel: "contents", icon: "contents" },
  { panel: "bookmarks", icon: "bookmarks" },
  { panel: "search", icon: "search" },
];

/** Grand écran : Rechercher et Signets en premier, Chat en dernier, à côté de son volet. */
const toolbarMenu = [menu[3]!, menu[2]!, menu[0]!, menu[1]!];

/** Entrée de menu proposée pour ce livre (signets en lecture complète, recherche selon la marque). */
const shown = (panel: MenuPanel, enabled: { bookmarksEnabled: boolean; searchEnabled: boolean }) =>
  panel === "bookmarks" ? enabled.bookmarksEnabled : panel === "search" ? enabled.searchEnabled : true;

const labelKey = (panel: MenuPanel, notesEnabled: boolean) => (panel === "bookmarks" && notesEnabled ? "notes" : panel);

export interface ReadAloudControl {
  reading: boolean;
  onToggle(): void;
}

/** « Lire à voix haute », ou « Arrêter la lecture » pendant que la voix lit. */
function ReadAloudButton({ reading, onToggle, large = false }: ReadAloudControl & { large?: boolean }) {
  const t = useT(messages);
  const label = reading ? t("readAloudStop") : t("readAloud");
  return (
    <button type="button" class={large ? "msb-pill msb-pill--large" : "msb-pill"} aria-pressed={reading} title={label} onClick={onToggle}>
      <Icon name={reading ? "stop" : "playArrow"} />
      <span class="msb-pill__label">{label}</span>
    </button>
  );
}

/** Barre du haut sur grand écran : mêmes boutons, formes et icônes que le menu de l'app. */
export function ReaderToolbar(props: Props) {
  const { title, section, excerpt, backUrl, bookmarked, bookmarksEnabled, chatAvailable, chatOpen, openPanel, embedded } = props;
  const t = useT(messages);
  return (
    <header class="msb-toolbar">
      <div class="msb-toolbar__start">
        {backUrl && (
          <a class="msb-round-button" href={backUrl} aria-label={t("closeBook")}>
            <Icon name="close" />
          </a>
        )}
        {bookmarksEnabled && (
          <button
            type="button"
            class="msb-round-button"
            aria-pressed={bookmarked}
            aria-label={t(bookmarked ? "removeBookmark" : "addBookmark")}
            onClick={props.onToggleBookmark}
          >
            <Icon name={bookmarked ? "bookmark" : "bookmarkAdd"} />
          </button>
        )}
      </div>
      {embedded ? (
        <span />
      ) : (
        <button
          ref={props.buttonRefs.book}
          type="button"
          class="msb-toolbar__title"
          aria-expanded={openPanel === "book"}
          aria-haspopup="dialog"
          title={t("book")}
          onClick={(e) => props.onPanel("book", e.currentTarget)}
        >
          <span class="msb-toolbar__book">
            {title}
            {excerpt && <span class="msb-badge">{t("excerpt")}</span>}
          </span>
          {section && <span class="msb-toolbar__section">{section}</span>}
        </button>
      )}
      <nav class="msb-toolbar__menu" aria-label={t("bookMenu")}>
        {toolbarMenu.map((item) =>
          !shown(item.panel, props) ? null : (
            <button
              key={item.panel}
              ref={props.buttonRefs[item.panel]}
              type="button"
              class="msb-pill"
              aria-expanded={openPanel === item.panel}
              aria-haspopup="dialog"
              title={t(labelKey(item.panel, props.notesEnabled))}
              onClick={(e) => props.onPanel(item.panel, e.currentTarget)}
            >
              <Icon name={item.icon} />
              <span class="msb-pill__label">{t(labelKey(item.panel, props.notesEnabled))}</span>
            </button>
          ),
        )}
        {props.readAloud && <ReadAloudButton {...props.readAloud} />}
        {!embedded && chatAvailable && (
          <button
            type="button"
            class="msb-pill msb-pill--primary"
            aria-pressed={chatOpen}
            onClick={props.onToggleChat}
            title={t(chatOpen ? "hideChat" : "showChat")}
          >
            <Icon name="chat" />
            <span class="msb-pill__label">{t("chat")}</span>
          </button>
        )}
        {!embedded && (
          <button
            type="button"
            class="msb-round-button"
            onClick={props.onFullscreen}
            aria-label={t("fullscreenLabel")}
            title={t("fullscreen")}
          >
            <Icon name="fullscreen" />
          </button>
        )}
      </nav>
    </header>
  );
}

interface MobileProps {
  title: string;
  section?: string;
  backUrl?: string;
  bookmarked: boolean;
  bookmarksEnabled: boolean;
  searchEnabled: boolean;
  notesEnabled: boolean;
  chatAvailable: boolean;
  onToggleBookmark(): void;
  readAloud?: ReadAloudControl;
  onChat(): void;
  onPanel(panel: MenuPanel): void;
}

/** Interface mobile affichée par un tap au centre : barre flottante en haut, menu en bas à droite, comme l'app. */
export function MobileChrome(props: MobileProps) {
  const t = useT(messages);
  return (
    <>
      <header class="msb-mobile-top">
        {props.bookmarksEnabled ? (
          <button
            type="button"
            class="msb-round-button msb-round-button--large"
            aria-pressed={props.bookmarked}
            aria-label={t(props.bookmarked ? "removeBookmark" : "addBookmark")}
            onClick={props.onToggleBookmark}
          >
            <Icon name={props.bookmarked ? "bookmark" : "bookmarkAdd"} size={32} />
          </button>
        ) : (
          <span class="msb-round-spacer" />
        )}
        <button type="button" class="msb-mobile-top__title" aria-haspopup="dialog" title={t("book")} onClick={() => props.onPanel("book")}>
          <span class="msb-toolbar__book">{props.title}</span>
          {props.section && <span class="msb-toolbar__section">{props.section}</span>}
        </button>
        {props.backUrl ? (
          <a class="msb-round-button msb-round-button--large" href={props.backUrl} aria-label={t("closeBook")}>
            <Icon name="close" size={32} />
          </a>
        ) : (
          <span class="msb-round-spacer" />
        )}
      </header>
      <nav class="msb-fab-menu" aria-label={t("bookMenu")}>
        {props.readAloud && <ReadAloudButton {...props.readAloud} large />}
        {props.chatAvailable && (
          <button type="button" class="msb-pill msb-pill--primary msb-pill--large" onClick={props.onChat}>
            <Icon name="chat" />
            <span class="msb-pill__label">{t("chat")}</span>
          </button>
        )}
        {menu.map((item) =>
          !shown(item.panel, props) ? null : (
            <button
              key={item.panel}
              type="button"
              class="msb-pill msb-pill--large"
              aria-haspopup="dialog"
              onClick={() => props.onPanel(item.panel)}
            >
              <Icon name={item.icon} />
              <span class="msb-pill__label">{t(labelKey(item.panel, props.notesEnabled))}</span>
            </button>
          ),
        )}
      </nav>
    </>
  );
}
